import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useCompanyId } from '../hooks/useCompanyId';
import { useSuccessManagerQuestionAssignee } from '../hooks/useSuccessManagerQuestionAssignee';
import { db } from '../firebase.js';
import { collection, addDoc, doc, updateDoc, getDoc, setDoc, query, where, getDocs } from 'firebase/firestore';
import { createQuestion } from '../services/firebase/questions';
import { BBA_CHECK_IN_CALENDLY_URL, openCalendlyPopup } from '../utils/calendlyWidget.js';
import SuccessManagerQuestionModal from './SuccessManagerQuestionModal';

function checkInFollowUpsStorageKey(surveyId) {
  return `checkInFollowUps:${surveyId}`;
}

const SurveyPopup = ({ isOpen, onClose, surveyId }) => {
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();
  const { companyId } = useCompanyId();
  const { assignee: successManagerQuestionAssignee } = useSuccessManagerQuestionAssignee(
    currentUser,
    companyId
  );
  const [successManagerQuestionModalOpen, setSuccessManagerQuestionModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [surveyData, setSurveyData] = useState(null);
  const [userResponses, setUserResponses] = useState({
    overall: { rating: 0 },
    sales: { rating: 0 },
    operations: { rating: 0 },
    finance: { rating: 0 },
    technology: { rating: 0 },
    marketing: { rating: 0 },
    hr: { rating: 0 },
    categorizedSatisfaction: {
      processEase: { selected: null },
      communication: { selected: null },
      value: { selected: null },
      supportResponsiveness: { selected: null },
      quality: { selected: null },
      timeliness: { selected: null }
    },
    churnRiskFactors: {
      price: { selected: false },
      quality: { selected: false },
      changingNeeds: { selected: false },
      poorCommunication: { selected: false },
      lackOfFeatures: { selected: false },
      trustConsistency: { selected: false }
    },
    additionalFeedback: ''
  });
  const [loading, setLoading] = useState(false);
  const [isCheckIn, setIsCheckIn] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [calUserProfile, setCalUserProfile] = useState(null);

  useEffect(() => {
    if (!isOpen || !currentUser?.uid) return;
    let cancelled = false;
    (async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (!cancelled && userDoc.exists()) setCalUserProfile(userDoc.data());
      } catch (e) {
        console.error('SurveyPopup: load user profile for Calendly', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, currentUser?.uid]);

  const persistCheckInFollowUp = (patch) => {
    if (!surveyId) return;
    const key = checkInFollowUpsStorageKey(surveyId);
    let o = {};
    try {
      o = JSON.parse(sessionStorage.getItem(key) || '{}');
    } catch (_) {
      o = {};
    }
    sessionStorage.setItem(key, JSON.stringify({ ...o, ...patch }));
  };

  const openCheckInCallCalendly = () => {
    const firstName =
      calUserProfile?.firstName?.trim() ||
      currentUser?.displayName?.split(/\s+/)[0] ||
      currentUser?.email?.split('@')[0] ||
      '';
    const lastName =
      calUserProfile?.lastName?.trim() ||
      (currentUser?.displayName?.split(/\s+/)?.slice(1)?.join(' ') || '').trim() ||
      '';
    const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
    const prefill =
      currentUser || calUserProfile
        ? {
            firstName,
            lastName,
            name: fullName || currentUser?.displayName || currentUser?.email?.split('@')[0] || '',
            email: currentUser?.email || '',
            phone: calUserProfile?.phone?.trim() || '',
          }
        : {};
    const hasPrefill =
      prefill.name || prefill.firstName || prefill.lastName || prefill.email || prefill.phone;
    openCalendlyPopup(BBA_CHECK_IN_CALENDLY_URL, hasPrefill ? prefill : undefined);
  };

  const handleSuccessManagerQuestionSubmit = async (questionText) => {
    if (!currentUser) {
      alert('You must be logged in to submit a question.');
      return;
    }
    if (!questionText?.trim()) {
      alert('Please enter your question.');
      return;
    }
    if (!successManagerQuestionAssignee?.id) {
      alert('No Success Manager is assigned to your account yet. Please contact support.');
      return;
    }
    try {
      const questionData = {
        accountId: companyId || currentUser.uid,
        accountName: currentUser.displayName || currentUser.email?.split('@')[0] || 'Unknown User',
        assigneeId: successManagerQuestionAssignee.id,
        assigneeName: successManagerQuestionAssignee.name,
        questionText: questionText.trim(),
        createdBy: currentUser.uid,
        createdByName: currentUser.displayName || currentUser.email?.split('@')[0] || 'Unknown User',
        pillar: 'success_manager',
      };
      await createQuestion(questionData);
      persistCheckInFollowUp({ wantsSuccessManagerQuestion: true });
    } catch (error) {
      console.error('Error submitting Success Manager question:', error);
      alert('Failed to submit question. Please try again.');
    }
  };

  // Load survey template when component mounts
  useEffect(() => {
    if (isOpen && surveyId) {
      loadSurveyTemplate();
    }
  }, [isOpen, surveyId]);

  const loadSurveyTemplate = async () => {
    try {
      const surveyDoc = await getDoc(doc(db, 'surveys', surveyId));
      if (surveyDoc.exists()) {
        const surveyDocData = surveyDoc.data();
        setSurveyData(surveyDocData.template);
        setIsCheckIn(surveyDocData.type === 'check-in');
        // Reset responses for check-in surveys
        if (surveyDocData.type === 'check-in') {
          setUserResponses({
            overall: { rating: 0 },
            additionalFeedback: ''
          });
        }
      }
    } catch (error) {
      console.error('Error loading survey template:', error);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Save survey results
      const resultData = {
        userId: currentUser?.uid,
        surveyId: surveyId,
        responses: userResponses,
        submittedAt: new Date(),
        status: 'completed'
      };

      await addDoc(collection(db, 'surveyResults'), resultData);

      // Update client document to remove pending survey
      if (currentUser?.uid) {
        const clientRef = doc(db, 'clients', currentUser.uid);
        await setDoc(clientRef, {
          pendingSurvey: false,
          surveyId: null,
          lastSurveyCompleted: new Date()
        }, { merge: true });
      }

      // Update check-in record if this is a check-in survey
      if (isCheckIn) {
        try {
          // Find the check-in record by surveyId
          const checkInsRef = collection(db, 'check_ins');
          // Scoped to the company: clients may only read their own company's check-ins
          const checkInQuery = query(checkInsRef, where('surveyId', '==', surveyId), where('accountId', '==', companyId || ''));
          const checkInSnapshot = await getDocs(checkInQuery);
          
          if (!checkInSnapshot.empty) {
            const checkInDoc = checkInSnapshot.docs[0];
            const rating = userResponses.overall.rating;
            
            // Convert rating to overallStatus
            let overallStatus = 'Okay';
            if (rating >= 4) {
              overallStatus = 'Good';
            } else if (rating <= 2) {
              overallStatus = 'Not Good';
            }

            // Update the check-in record
            let followUps = { wantsSuccessManagerQuestion: false, requestedCheckInCall: false };
            if (surveyId) {
              try {
                followUps = {
                  ...followUps,
                  ...JSON.parse(sessionStorage.getItem(checkInFollowUpsStorageKey(surveyId)) || '{}'),
                };
              } catch (_) {
                followUps = { wantsSuccessManagerQuestion: false, requestedCheckInCall: false };
              }
            }

            await updateDoc(checkInDoc.ref, {
              userId: currentUser?.uid,
              overallStatus: overallStatus,
              status: 'completed',
              submittedAt: new Date(),
              rating: rating,
              additionalFeedback: userResponses.additionalFeedback || '',
              wantsSuccessManagerQuestion: !!followUps.wantsSuccessManagerQuestion,
              requestedCheckInCall: !!followUps.requestedCheckInCall,
            });
            if (surveyId) {
              try {
                sessionStorage.removeItem(checkInFollowUpsStorageKey(surveyId));
              } catch (_) {}
            }
          }
        } catch (error) {
          console.error('Error updating check-in record:', error);
          // Don't fail the submission if check-in update fails
        }
      }

      // Show thank you page briefly then close
      if (isCheckIn) {
        // For check-in surveys, show thank you message then close
        setShowThankYou(true);
        setTimeout(() => {
          onClose();
          setShowThankYou(false);
          setUserResponses({
            overall: { rating: 0 },
            additionalFeedback: ''
          });
        }, 2000);
      } else {
        setCurrentPage(4);
        setTimeout(() => {
          onClose();
          setCurrentPage(0);
          setUserResponses({
            overall: { rating: 0 },
            sales: { rating: 0 },
            operations: { rating: 0 },
            finance: { rating: 0 },
            technology: { rating: 0 },
            marketing: { rating: 0 },
            hr: { rating: 0 },
            categorizedSatisfaction: {
              processEase: { selected: null },
              communication: { selected: null },
              value: { selected: null },
              supportResponsiveness: { selected: null },
              quality: { selected: null },
              timeliness: { selected: null }
            },
            churnRiskFactors: {
              price: { selected: false },
              quality: { selected: false },
              changingNeeds: { selected: false },
              poorCommunication: { selected: false },
              lackOfFeatures: { selected: false },
              trustConsistency: { selected: false }
            },
            additionalFeedback: ''
          });
        }, 3000);
      }
    } catch (error) {
      console.error('Error submitting survey:', error);
      alert('Error submitting survey. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !surveyData) return null;

  // Check-in survey (simplified format)
  if (isCheckIn) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto`}>
          <div className="flex items-center justify-center mb-6">
            <div className="flex flex-col items-center">
              <img 
                src="/bba-emblem.svg" 
                alt="BBA Emblem" 
                className="h-16 w-auto mb-3"
              />
              <h3 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-800'} text-center`}>
                Feedback Check-In
              </h3>
            </div>
          </div>

          {showThankYou ? (
            <div className="space-y-6 text-center">
              <div className="mb-6">
                <svg className="w-16 h-16 text-green-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 className={`text-2xl font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                  Thank You!
                </h3>
                <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Thank you for your feedback. Your responses have been submitted successfully.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <h5 className={`text-xl font-medium mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                {surveyData.overall?.question || "How satisfied are you with our overall service?"}{' '}
                <span className="text-red-600 dark:text-red-400" aria-hidden>
                  *
                </span>
              </h5>
              <div className="flex space-x-2 justify-center mb-6">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    className={`text-5xl transition-colors duration-200 ${
                      userResponses.overall.rating >= star
                        ? 'text-yellow-400'
                        : 'text-gray-200 hover:text-yellow-400'
                    }`}
                    onClick={() => {
                      setUserResponses({
                        ...userResponses,
                        overall: { rating: star }
                      });
                    }}
                  >
                    ★
                  </button>
                ))}
              </div>
              
              {/* Optional Text Box */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Additional Feedback (Optional)
                </label>
                <textarea
                  className={`w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500 resize-none`}
                  rows="4"
                  placeholder="Share any additional thoughts or feedback..."
                  value={userResponses.additionalFeedback || ''}
                  onChange={(e) => setUserResponses({
                    ...userResponses,
                    additionalFeedback: e.target.value
                  })}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                <button
                  type="button"
                  onClick={() => setSuccessManagerQuestionModalOpen(true)}
                  className={`w-full px-4 py-3 rounded-lg border-2 text-sm font-medium transition-colors ${
                    darkMode
                      ? 'border-gray-500 text-gray-100 hover:bg-gray-700'
                      : 'border-gray-300 text-gray-800 hover:bg-gray-50'
                  }`}
                >
                  I Have A Question
                </button>
                <button
                  type="button"
                  onClick={() => {
                    persistCheckInFollowUp({ requestedCheckInCall: true });
                    openCheckInCallCalendly();
                  }}
                  className={`w-full px-4 py-3 rounded-lg border-2 text-sm font-medium transition-colors ${
                    darkMode
                      ? 'border-gray-500 text-gray-100 hover:bg-gray-700'
                      : 'border-gray-300 text-gray-800 hover:bg-gray-50'
                  }`}
                >
                  I&apos;d Like to Set Up a Check In Call
                </button>
              </div>

              <div className="flex justify-end mt-6">
                <button
                  onClick={handleSubmit}
                  disabled={loading || userResponses.overall.rating === 0}
                  className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            </div>
          )}
        </div>
        <SuccessManagerQuestionModal
          isOpen={successManagerQuestionModalOpen}
          onClose={() => setSuccessManagerQuestionModalOpen(false)}
          onSubmit={handleSuccessManagerQuestionSubmit}
          assigneeName={successManagerQuestionAssignee?.name}
        />
      </div>
    );
  }

  // Regular survey (original multi-page format)
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto`}>
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex-1"></div>
          <h3 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-800'} text-center flex-1`}>
            Client Satisfaction Survey
          </h3>
          <button
            onClick={onClose}
            className={`p-2 ${darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'} flex-1 flex justify-end`}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Progress Bar */}
        {currentPage >= 1 && currentPage <= 3 && (
          <div className="mb-6">
            <div className="flex justify-center mb-2">
              <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {currentPage === 1 ? '1/3' : currentPage === 2 ? '2/3' : '3/3'}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-red-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${currentPage === 1 ? '33.33%' : currentPage === 2 ? '66.66%' : '100%'}` }}
              ></div>
            </div>
          </div>
        )}

        {/* Page 0: Introduction */}
        {currentPage === 0 && (
          <div className="space-y-8 text-center">
            <div className="mb-8">
              <svg className="w-24 h-24 text-red-500 mx-auto mb-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className={`text-3xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                Feedback Survey
              </h3>
              <p className={`text-lg ${darkMode ? 'text-gray-300' : 'text-gray-600'} mb-6`}>
                We value your feedback! Please take a moment to complete this survey to help us improve our services.
              </p>
              
              <div className={`inline-flex items-center space-x-3 ${darkMode ? 'bg-gray-700' : 'bg-gray-100'} px-6 py-3 rounded-lg`}>
                <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                  Estimated time: 1 minute
                </span>
              </div>
            </div>
            
            <button
              onClick={() => setCurrentPage(1)}
              className="px-8 py-4 bg-red-600 hover:bg-red-700 text-white text-lg font-semibold rounded-lg transition-colors duration-200 shadow-lg hover:shadow-xl"
            >
              Start Survey
            </button>
          </div>
        )}

        {/* Page 1: Overall & Departments */}
        {currentPage === 1 && (
          <div className="space-y-6">
            <h4 className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-800'} text-center`}>Department Satisfaction</h4>
            
            {/* Overall Section */}
            <div className={`${darkMode ? 'bg-gray-700' : 'bg-gray-100'} p-4 rounded-lg`}>
              <h4 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>Overall</h4>
              <h5 className={`font-medium mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                {surveyData.overall.question}
              </h5>
              <div className="flex space-x-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    className={`text-5xl transition-colors duration-200 ${
                      userResponses.overall.rating >= star
                        ? 'text-yellow-400'
                        : 'text-gray-200 hover:text-yellow-400'
                    }`}
                    onClick={() => {
                      setUserResponses({
                        ...userResponses,
                        overall: { rating: star }
                      });
                    }}
                  >
                    ★
                  </button>
                ))}
              </div>
            </div>

            {/* Departments */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {['sales', 'operations', 'finance', 'technology', 'marketing', 'hr'].map((dept) => (
                <div key={dept} className={`p-4 rounded-lg ${
                  dept === 'sales' ? (darkMode ? 'bg-red-900' : 'bg-red-50') :
                  dept === 'operations' ? (darkMode ? 'bg-blue-900' : 'bg-blue-50') :
                  dept === 'finance' ? (darkMode ? 'bg-green-900' : 'bg-green-50') :
                  dept === 'technology' ? (darkMode ? 'bg-purple-900' : 'bg-purple-50') :
                  dept === 'marketing' ? (darkMode ? 'bg-orange-900' : 'bg-orange-50') :
                  (darkMode ? 'bg-yellow-900' : 'bg-yellow-50')
                }`}>
                  <h4 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                    {dept === 'hr' ? 'HR' : dept.charAt(0).toUpperCase() + dept.slice(1)}
                  </h4>
                  <h5 className={`font-medium mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                    {surveyData[dept].question}
                  </h5>
                  <div className="flex space-x-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        className={`text-5xl transition-colors duration-200 ${
                          userResponses[dept].rating >= star
                            ? 'text-yellow-400'
                            : 'text-gray-200 hover:text-yellow-400'
                        }`}
                        onClick={() => {
                          setUserResponses({
                            ...userResponses,
                            [dept]: { rating: star }
                          });
                        }}
                      >
                        ★
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Page 2: Categorized Satisfaction */}
        {currentPage === 2 && (
          <div className="space-y-6">
            <h4 className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-800'} text-center`}>Service Satisfaction</h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {Object.entries(surveyData.categorizedSatisfaction).map(([key, category]) => (
                <div key={key} className={`${darkMode ? 'bg-gray-700' : 'bg-gray-100'} p-4 rounded-lg`}>
                  <h4 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                    {key === 'processEase' ? 'Process Ease' :
                      key === 'communication' ? 'Communication' :
                      key === 'value' ? 'Value' :
                      key === 'supportResponsiveness' ? 'Support Responsiveness' :
                      key === 'quality' ? 'Quality' :
                      key === 'timeliness' ? 'Timeliness' : key}
                  </h4>
                  <h5 className={`font-medium mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                    {category.question}
                  </h5>
                  <div className="flex space-x-4">
                    {['Satisfied', 'Neutral', 'Unsatisfied'].map((option) => {
                      const isSelected = userResponses.categorizedSatisfaction[key].selected === option;
                      
                      let buttonClasses = 'px-6 py-3 rounded-lg font-medium transition-all duration-200 border-2 ';
                      
                      if (option === 'Satisfied') {
                        if (isSelected) {
                          buttonClasses += 'bg-green-500 text-white border-green-500';
                        } else {
                          buttonClasses += 'bg-transparent text-green-500 border-green-500 hover:bg-green-50';
                        }
                      } else if (option === 'Neutral') {
                        if (isSelected) {
                          buttonClasses += 'bg-yellow-500 text-white border-yellow-500';
                        } else {
                          buttonClasses += 'bg-transparent text-yellow-500 border-yellow-500 hover:bg-yellow-50';
                        }
                      } else if (option === 'Unsatisfied') {
                        if (isSelected) {
                          buttonClasses += 'bg-red-500 text-white border-red-500';
                        } else {
                          buttonClasses += 'bg-transparent text-red-500 border-red-500 hover:bg-red-50';
                        }
                      }
                      
                      return (
                        <button
                          key={option}
                          type="button"
                          className={buttonClasses}
                          onClick={() => {
                            setUserResponses({
                              ...userResponses,
                              categorizedSatisfaction: {
                                ...userResponses.categorizedSatisfaction,
                                [key]: { selected: option }
                              }
                            });
                          }}
                        >
                          {option}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Page 3: Concerns */}
        {currentPage === 3 && (
          <div className="space-y-6">
            <h4 className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-800'} text-center`}>Concerns</h4>
            <p className={`text-center ${darkMode ? 'text-gray-300' : 'text-gray-600'} mb-6`}>
              Select all concerns you have with Builders Breakthrough Advisors
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(surveyData.churnRiskFactors).map(([key, factor]) => (
                <button
                  key={key}
                  type="button"
                  className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                    userResponses.churnRiskFactors[key].selected
                      ? 'bg-red-500 text-white border-red-500'
                      : 'bg-transparent text-red-500 border-red-500 hover:bg-red-50'
                  }`}
                  onClick={() => {
                    setUserResponses({
                      ...userResponses,
                      churnRiskFactors: {
                        ...userResponses.churnRiskFactors,
                        [key]: {
                          ...userResponses.churnRiskFactors[key],
                          selected: !userResponses.churnRiskFactors[key].selected
                        }
                      }
                    });
                  }}
                >
                  <span className="font-medium">{factor.question}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Page 4: Thank You */}
        {currentPage === 4 && (
          <div className="space-y-6 text-center">
            <div className="mb-6">
              <svg className="w-16 h-16 text-green-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className={`text-2xl font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                Thank You!
              </h3>
              <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Thank you for your feedback. Your responses have been submitted successfully.
              </p>
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        {currentPage !== 0 && currentPage !== 4 && (
          <div className="flex justify-between mt-8">
            <button
              onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
              disabled={currentPage === 0}
              className={`px-4 py-2 rounded-md transition-colors duration-200 ${
                currentPage === 0
                  ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                  : 'bg-red-600 hover:bg-red-700 text-white'
              }`}
            >
              Previous
            </button>
            
            {currentPage === 3 ? (
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md transition-colors duration-200 disabled:opacity-50"
              >
                {loading ? 'Submitting...' : 'Submit'}
              </button>
            ) : (
              <button
                onClick={() => setCurrentPage(Math.min(4, currentPage + 1))}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors duration-200"
              >
                Next
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SurveyPopup;
